import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { KeyRound, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import api from "../../utils/Api";

const ACCENT = "#FF7A00";
const NAVY   = "#0F172A";
const MUTED  = "#64748B";
const BORDER = "#E2E8F0";

function ResetPasswordConfirm() {
  const [params]            = useSearchParams();
  const token               = params.get("token");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPwd, setShowPwd]     = useState(false);
  const [message, setMessage]     = useState("");
  const [success, setSuccess]     = useState(false);
  const [loading, setLoading]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (password.length < 8) {
      setMessage("La contraseña debe tener al menos 8 caracteres.");
      setSuccess(false);
      return;
    }
    if (password !== confirm) {
      setMessage("Las contraseñas no coinciden.");
      setSuccess(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/reset-password/confirm", {
        token, password, password_confirmation: confirm,
      });
      setMessage(res.data.message || "Contraseña restablecida correctamente.");
      setSuccess(true);
      setPassword(""); setConfirm("");
    } catch (error) {
      setMessage(error.response?.data?.message || "Error al cambiar la contraseña.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (hasErr) => ({
    width: "100%", padding: "10px 4px",
    background: "transparent", border: "none",
    borderBottom: `2px solid ${hasErr ? "#EF4444" : BORDER}`,
    fontSize: "15px", color: NAVY, outline: "none",
    fontFamily: "inherit", transition: "border-color 0.15s",
    boxSizing: "border-box",
  });

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Panel izquierdo */}
      <div className="tn-auth-left" style={{
        flex: "0 0 48%",
        background: "linear-gradient(145deg, #0F1629 0%, #1A2347 50%, #1E3A8A 100%)",
        display: "flex", flexDirection: "column", padding: "48px 56px",
        position: "relative", overflow: "hidden",
      }}>
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagRPC" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
              <line x1="0"  y1="0" x2="0"  y2="120" stroke="white" strokeWidth="1"/>
              <line x1="40" y1="0" x2="40" y2="120" stroke="white" strokeWidth="1"/>
              <line x1="80" y1="0" x2="80" y2="120" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagRPC)" />
        </svg>
        <div style={{
          position: "absolute", bottom: "-60px", left: "-60px",
          width: "280px", height: "280px",
          background: "radial-gradient(circle, rgba(255,122,0,0.15) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
          <svg width="56" height="56" viewBox="0 0 64 64" fill="none" style={{ marginBottom: "40px" }}>
            <line x1="32" y1="4"  x2="32" y2="60" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="4"  y1="32" x2="60" y2="32" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="11" y1="11" x2="53" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="53" y1="11" x2="11" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
          </svg>

          <h2 style={{
            fontSize: "clamp(32px, 3.5vw, 46px)", fontWeight: "800",
            color: "#FFFFFF", lineHeight: "1.1", letterSpacing: "-1.5px",
            margin: "0 0 16px", fontStyle: "italic"
          }}>
            Nueva clave,<br />
            <span style={{ color: ACCENT }}>nueva etapa.</span>
          </h2>
          <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.55)", lineHeight: "1.7", maxWidth: "300px", margin: 0 }}>
            Elige una contraseña segura. Una vez guardada podrás acceder con normalidad a tu cuenta.
          </p>
        </div>

        <p style={{ position: "relative", zIndex: 1, fontSize: "12px", color: "rgba(255,255,255,0.25)", margin: 0, marginTop: "48px" }}>
          © 2026 Turnora. Todos los derechos reservados.
        </p>
      </div>

      {/* Panel derecho */}
      <div style={{
        flex: 1, background: "#FFFFFF",
        display: "flex", flexDirection: "column", padding: "40px 56px",
      }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "auto" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" class="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
          <span style={{ fontSize: "16px", fontWeight: "700", color: NAVY, letterSpacing: "-0.3px" }}>Turnora</span>
        </div>

        {/* Form */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: "360px", width: "100%" }}>

          <div style={{
            width: "56px", height: "56px", borderRadius: "14px",
            background: "#FFF7ED", display: "flex", alignItems: "center",
            justifyContent: "center", marginBottom: "24px"
          }}>
            <KeyRound size={26} style={{ color: ACCENT }} />
          </div>

          <h1 style={{ fontSize: "28px", fontWeight: "800", color: NAVY, margin: "0 0 8px", letterSpacing: "-0.8px" }}>
            Nueva contraseña
          </h1>
          <p style={{ fontSize: "14px", color: MUTED, margin: "0 0 32px" }}>
            Introduce y confirma tu nueva contraseña de acceso.
          </p>

          {message && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "12px 14px", marginBottom: "24px",
              background: success ? "#F0FDF4" : "#FEF2F2",
              border: `1px solid ${success ? "#BBF7D0" : "#FECACA"}`,
              borderRadius: "8px"
            }}>
              {success
                ? <CheckCircle size={16} style={{ color: "#10B981", flexShrink: 0, marginTop: "1px" }} />
                : <AlertCircle  size={16} style={{ color: "#EF4444", flexShrink: 0, marginTop: "1px" }} />
              }
              <span style={{ fontSize: "13px", color: success ? "#15803D" : "#7F1D1D", fontWeight: "500" }}>
                {message}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Nueva contraseña */}
            <div style={{ marginBottom: "24px" }}>
              <label style={{ fontSize: "11px", fontWeight: "700", color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                Nueva contraseña
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  style={{ ...inputStyle(false), paddingRight: "36px" }}
                  onFocus={(e) => e.target.style.borderBottomColor = ACCENT}
                  onBlur={(e)  => e.target.style.borderBottomColor = BORDER}
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                  position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", color: MUTED, cursor: "pointer",
                  display: "flex", padding: "4px"
                }}>
                  {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {/* Confirmar */}
            <div style={{ marginBottom: "36px" }}>
              <label style={{ fontSize: "11px", fontWeight: "700", color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                Confirmar contraseña
              </label>
              <input
                type={showPwd ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                style={inputStyle(false)}
                onFocus={(e) => e.target.style.borderBottomColor = ACCENT}
                onBlur={(e)  => e.target.style.borderBottomColor = BORDER}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: "52px",
                background: loading ? "#CBD5E1" : NAVY,
                color: "white", border: "none", borderRadius: "10px",
                fontSize: "15px", fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "background 0.15s",
                letterSpacing: "-0.2px", marginBottom: "20px"
              }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#1E2947")}
              onMouseLeave={(e) => !loading && (e.currentTarget.style.background = loading ? "#CBD5E1" : NAVY)}
            >
              {loading ? "Guardando..." : "Guardar nueva contraseña"}
            </button>
          </form>

          <p style={{ textAlign: "center", fontSize: "13px", color: MUTED, margin: 0 }}>
            <Link to="/" style={{ color: ACCENT, fontWeight: "600", textDecoration: "none" }}>
              ← Volver al inicio de sesión
            </Link>
          </p>
        </div>

        <div style={{ marginTop: "auto" }} />
      </div>

      <style>{`
        @media (max-width: 860px) { .tn-auth-left { display: none !important; } }
      `}</style>
    </div>
  );
}

export default ResetPasswordConfirm;
